import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faGift,
  faHourglass1,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { AnyRewardConfig } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { range } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { EligibleCollectionsDialog } from './EligibleCollectionsDialog/index.tsx';
import type { CartPromo_cartPurchaseRewardPromotionalEvent } from './__generated__/index.graphql.ts';

type EligibilityRules =
  CartPromo_cartPurchaseRewardPromotionalEvent['eligibilityRules'];

const Sticky = styled(Horizontal)`
  width: 100%;
  background-color: var(--c-black);
  @media ${laptopAndAbove} {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const Container = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  isolation: isolate;
  width: 100%;
  padding: var(--double-unit);
  background: linear-gradient(
    84deg,
    rgba(248, 211, 218, 0.12) 0%,
    rgba(179, 169, 244, 0.12) 28.32%,
    rgba(251, 233, 251, 0.12) 54.01%,
    rgba(79, 148, 253, 0.12) 100%
  );
`;

const TitleContainer = styled(Vertical)`
  align-items: center;
  @media ${laptopAndAbove} {
    padding: 0 var(--double-unit);
    flex-direction: row;
    align-items: baseline;
  }
`;

const StyledLabelL = styled(LabelL)`
  background: linear-gradient(
    84deg,
    #f8d3da 0%,
    #b3a9f4 28.32%,
    #fbe9fb 54.01%,
    #4f94fd 100%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const InfoAbsolute = styled(Horizontal)`
  display: none;
  @media ${laptopAndAbove} {
    position: absolute;
    top: var(--unit);
    right: var(--unit);
    display: flex;
    width: 100%;
    justify-content: flex-end;
  }
`;

const Content = styled(Vertical).attrs({
  center: true,
})`
  width: 100%;
`;
const TopLine = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: [empty] var(--double-unit) [chip] 1fr [info] var(
      --double-unit
    );
  @media ${laptopAndAbove} {
    width: auto;
    display: block;
  }
`;

const AreaChip = styled.div`
  grid-area: chip;
`;

const AreaInfo = styled.div`
  grid-area: info;
  @media ${laptopAndAbove} {
    display: none;
  }
`;

const textUnderline = css`
  border-bottom: 1px solid;
  border-image-slice: 1;
  border-image-source: linear-gradient(
    84deg,
    #f8d3da 0%,
    #b3a9f4 28.32%,
    #fbe9fb 54.01%,
    #4f94fd 100%
  );
`;

const DiscoverEligibleCollection = styled.button`
  ${textUnderline}
`;
const RewardsLink = styled.a`
  ${textUnderline}
`;

const Rewards = styled(Horizontal)`
  display: inline-flex;
  vertical-align: text-bottom;
`;

const Thresold = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  border-bottom: 1px solid var(--c-nd-600);
  padding-bottom: var(--half-unit);
`;

type Props = {
  cartPurchaseRewardPromotionalEvent: CartPromo_cartPurchaseRewardPromotionalEvent;
};

enum CartPromoType {
  CARD_COUNT = 'CARD_COUNT',
  ALL_COLLECTION_COUNT = 'ALL_COLLECTION_COUNT',
  SCOPED_COLLECTION_COUNT = 'SCOPED_COLLECTION_COUNT',
  CART_AMOUNT = 'CART_AMOUNT',
}

const promoMessage = defineMessages({
  [CartPromoType.CARD_COUNT]: {
    id: 'CartPromo.title.cardsCount',
    defaultMessage: 'Buy {count}+ cards to win <rewards>rewards</rewards>',
  },
  [CartPromoType.ALL_COLLECTION_COUNT]: {
    id: 'CartPromo.title.collectionsCount',
    defaultMessage:
      'Buy {count}+ cards from the same collection to win <rewards>rewards</rewards>',
  },
  [CartPromoType.SCOPED_COLLECTION_COUNT]: {
    id: 'CartPromo.title.scopedCollectionsCount',
    defaultMessage:
      'Buy {count}+ cards from the same <eligibleCollectionsButton>eligible collection</eligibleCollectionsButton> to win <rewards>rewards</rewards>',
  },
  [CartPromoType.CART_AMOUNT]: {
    id: 'CartPromo.title.cartAmount',
    defaultMessage:
      'Spend more than {amount} to win <rewards>rewards</rewards>',
  },
});

const getPromotionDetails = (eligibilityRules: EligibilityRules) => {
  if (eligibilityRules?.eligibleCartMonetaryAmount) {
    return {
      type: CartPromoType.CART_AMOUNT,
      minimumAmount: eligibilityRules.eligibleCartMonetaryAmount,
    };
  }
  if (eligibilityRules?.eligibleCollections) {
    const count = eligibilityRules.eligibleCollections.cartCardsCount;
    const thresholds = range(count).map(i => ({
      score: i + 1,
    }));
    if (
      (eligibilityRules.eligibleCollections.cardCollections?.length || 0) > 0
    ) {
      return {
        type: CartPromoType.SCOPED_COLLECTION_COUNT,
        count,
        collections: eligibilityRules.eligibleCollections.cardCollections,
        thresholds,
      };
    }
    return {
      type: CartPromoType.ALL_COLLECTION_COUNT,
      count,
      thresholds,
    };
  }
  if (eligibilityRules?.eligibleCartCardsCount) {
    return {
      type: CartPromoType.CARD_COUNT,
      count: eligibilityRules.eligibleCartCardsCount,
      thresholds: range(eligibilityRules.eligibleCartCardsCount).map(i => ({
        score: i + 1,
      })),
    };
  }
  return null;
};

const deduplicateType = <T extends { __typename: string }>(configs: T[]): T[] =>
  Object.values(
    configs.reduce<Record<string, T>>((a, c) => {
      a[c.__typename] = c;
      return a;
    }, {})
  );

export const CartPromo = ({ cartPurchaseRewardPromotionalEvent }: Props) => {
  const { formatMessage } = useIntlContext();
  const [openCollections, setOpenCollections] = useState(false);
  const { toMonetaryAmount } = useMonetaryAmount();

  const { name, endDate, blogUrl, progression, eligibilityRules } =
    cartPurchaseRewardPromotionalEvent || {};
  const {
    score = 0,
    monetaryAmountScore,
    thresholds: fallbackThresholds = [],
  } = progression || {};

  const details = getPromotionDetails(eligibilityRules);

  const minimumAmount = details?.minimumAmount || MonetaryAmount.zero;
  const { main: mainMinimumAmount } = useAmountWithConversion({
    monetaryAmount: minimumAmount,
  });

  if (!details) return null;

  const {
    type,
    count,
    thresholds = (fallbackThresholds || []).map(t => {
      if (t.monetaryThreshold) {
        const rewardConfigs = deduplicateType(
          t.probableRewards.flatMap(r => r.rewardConfigs)
        );
        return {
          score: toMonetaryAmount(t.monetaryThreshold).usdCents,
          reward: (
            <Tooltip
              placement="top"
              title={
                <Vertical center>
                  <Thresold className="text-center">
                    <AmountWithConversion
                      monetaryAmount={t.monetaryThreshold}
                      hideExponent
                    />
                  </Thresold>
                  <Rewards>
                    {rewardConfigs
                      .map(r => (
                        <AnyRewardConfig
                          key={r.id}
                          anyRewardConfig={r}
                          rarity="mix"
                          hideText={!isType(r, 'ConversionCreditRewardConfig')}
                        />
                      ))
                      .reduce<
                        ReactNode[] | null
                      >((acc, x) => (acc === null ? [x] : [acc, formatMessage(glossary.or), x]), null)}
                  </Rewards>
                </Vertical>
              }
            >
              <FontAwesomeIcon icon={faGift} size="xl" />
            </Tooltip>
          ),
        };
      }
      return {
        score: t.threshold!,
      };
    }),
    collections,
  } = details;

  const currentScore =
    type === CartPromoType.CART_AMOUNT
      ? toMonetaryAmount(monetaryAmountScore).usdCents
      : score || 0;

  const allRewardConfigs =
    fallbackThresholds?.flatMap(t =>
      t.probableRewards.flatMap(r => r.rewardConfigs)
    ) || [];

  const rewardConfigs = deduplicateType(allRewardConfigs);

  const needsStockInfos = rewardConfigs.some(
    config =>
      isType(config, 'DeliverableItemRewardConfig') &&
      (isType(config.deliverableItem, 'TicketsDeliverableItem') ||
        isType(config.deliverableItem, 'JerseyDeliverableItem'))
  );

  const stocks =
    cartPurchaseRewardPromotionalEvent.deliverableItemsVariantsStocks?.filter(
      Boolean
    );

  const blogLinkNode = blogUrl ? (
    <a href={blogUrl} target="_blank" rel="noreferrer">
      <FontAwesomeIcon icon={faInfoCircle} color="var(--c-nd-600)" />
    </a>
  ) : null;

  return (
    <>
      <Sticky>
        <Container>
          <InfoAbsolute>{blogLinkNode}</InfoAbsolute>
          <Content gap={1.5}>
            <TitleContainer>
              <TopLine>
                <AreaChip className="text-center">
                  <Chip>
                    <FontAwesomeIcon icon={faHourglass1} />
                    <TimeLeft time={endDate} />
                  </Chip>
                </AreaChip>
                <AreaInfo>{blogLinkNode}</AreaInfo>
              </TopLine>
              <StyledLabelL className="text-center">
                <FormattedMessage
                  {...promoMessage[type]}
                  values={{
                    count,
                    rewards: chunks => (
                      <RewardsLink
                        type="button"
                        href={blogUrl || ''}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Tooltip
                          title={
                            <Rewards>
                              {rewardConfigs.map(r => (
                                <AnyRewardConfig
                                  key={r.id}
                                  anyRewardConfig={r}
                                  rarity="mix"
                                  hideText
                                />
                              ))}
                            </Rewards>
                          }
                        >
                          {chunks}
                        </Tooltip>
                      </RewardsLink>
                    ),
                    eligibleCollectionsButton: chunks => (
                      <DiscoverEligibleCollection
                        type="button"
                        onClick={() => setOpenCollections(true)}
                      >
                        {chunks}
                      </DiscoverEligibleCollection>
                    ),
                    amount: mainMinimumAmount,
                  }}
                />
              </StyledLabelL>
            </TitleContainer>
            <SplitProgressBar
              variant="oblique"
              color="iridescent"
              thresholds={thresholds}
              currentScore={currentScore}
              split
              glow
              gap={1}
              logarithmic={type === CartPromoType.CART_AMOUNT}
            />
          </Content>
        </Container>
      </Sticky>
      {type === CartPromoType.SCOPED_COLLECTION_COUNT && (
        <EligibleCollectionsDialog
          promotionName={name}
          collections={collections}
          needsStockInfos={needsStockInfos}
          stocks={stocks}
          open={openCollections}
          onClose={() => setOpenCollections(false)}
        />
      )}
    </>
  );
};

CartPromo.fragments = {
  cartPurchaseRewardPromotionalEvent: gql`
    fragment CartPromo_cartPurchaseRewardPromotionalEvent on CartPurchaseRewardPromotionalEvent {
      slug
      name
      blogUrl
      endDate
      eligibilityRules {
        id
        eligibleCollections {
          cartCardsCount
          cardCollections {
            slug
            rarity
            ...EligibleCollectionsDialog_cardCollection
          }
        }
        eligibleCartCardsCount
        eligibleCartMonetaryAmount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
      progression(cartId: $cartId) {
        id
        score
        monetaryAmountScore {
          ...useMonetaryAmount_monetaryAmount
        }
        thresholds {
          threshold
          monetaryThreshold {
            ...useMonetaryAmount_monetaryAmount
          }
          probableRewards {
            id
            rewardConfigs {
              id
              ...AnyRewardConfig_anyRewardConfigInterface
            }
          }
        }
      }
      deliverableItemsVariantsStocks {
        id
        ...EligibleCollectionsDialog_deliverableItemVariantsStocks
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${EligibleCollectionsDialog.fragments.cardCollection}
    ${EligibleCollectionsDialog.fragments.deliverableItemVariantsStocks}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<CartPromo_cartPurchaseRewardPromotionalEvent>,
};
